import { ProductFamilyKind } from "@redotech/redo-prisma-client/enums";
import {
  imageIdFormat,
  productFamilyIdFormat,
  productFamilyImageIdFormat,
  productFamilyOptionIdFormat,
  productFamilyOptionValueIdFormat,
  productIdFormat,
} from "@redotech/redo-product-id";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  AttributeSchema,
  AttributeType,
  AttributeValueSchema,
  AttributeValueType,
} from "../attributes";
import { ProductCategorySchema } from "../product-category";

export const ProductFamilyKindSchema = createEnum(ProductFamilyKind);

const ImageSchema_ = z.object({
  id: productFamilyImageIdFormat.schema,
  imageId: imageIdFormat.schema.nullish(),
  url: z.string(),
  altText: z.string(),
  thumbhash: z.string().nullish(),
  position: z.number(),
  generated: z.boolean(),
  feedback: z.enum(["positive", "negative"]).nullable(),
  isDefault: z.boolean(),
});
const ProductImageInFamilySchema_ = z.object({
  id: imageIdFormat.schema,
  url: z.string(),
  altText: z.string(),
  thumbhash: z.string().nullish(),
  position: z.number(),
  generated: z.boolean(),
  feedback: z.enum(["positive", "negative"]).nullable(),
  isDefault: z.boolean(),
});
const ProductInFamilySchema_ = z.object({
  id: productIdFormat.schema,
  title: z.string(),
  displayTitle: z.string(),
  upc: z.string().nullish(),
  sku: z.string().nullish(),
  price: z.number().nullish(),
  cost: z.number().nullish(),
  currency: z.string().nullish(),
  image: ProductImageInFamilySchema_.nullish(),
  availableInventory: z.number(),
  options: z.array(z.object({ name: z.string(), value: z.string() })),
});
const ProductOptionValueSchema_ = z.object({
  id: productFamilyOptionValueIdFormat.schema,
  value: z.string(),
});
export const ProductOptionValueSchema: z.ZodType<ProductOptionValue> =
  ProductOptionValueSchema_;
export interface ProductOptionValue extends z.infer<
  typeof ProductOptionValueSchema_
> {}
const ProductOptionsSchema_ = z.object({
  id: productFamilyOptionIdFormat.schema,
  name: z.string(),
  values: z.array(ProductOptionValueSchema_),
});

export const ProductOptionsSchema: z.ZodType<ProductOptions> =
  ProductOptionsSchema_;
export interface ProductOptions extends z.infer<typeof ProductOptionsSchema_> {}

const ProductFamilyDetailsSchema_ = z.object({
  id: productFamilyIdFormat.schema,
  title: z.string(),
  description: z.string(),
  vendor: z.string().nullable(),
  tags: z.array(z.string()),
  kind: ProductFamilyKindSchema,
  created_at: z.date(),
  updated_at: z.date(),
  images: z.array(ImageSchema_),
  category: ProductCategorySchema.nullable(),
  products: z.array(ProductInFamilySchema_),
  attributes: z.array(AttributeSchema),
  options: z.array(ProductOptionsSchema_),
});

export const ProductFamilyDetailsSchema: z.ZodType<ProductFamilyDetails> =
  ProductFamilyDetailsSchema_;
export interface ProductFamilyDetails extends z.infer<
  typeof ProductFamilyDetailsSchema_
> {}

export type ProductFamilyDetailsForEmbeddingContent = Omit<
  ProductFamilyDetails,
  "attributes" | "products"
>;

export enum GeneralProductFamilyField {
  title = "title",
  description = "description",
  vendor = "vendor",
}

export const GeneralProductFamilyFieldSchema = createEnum(
  GeneralProductFamilyField,
);
export type GeneralProductFamilyFieldType = z.infer<
  typeof GeneralProductFamilyFieldSchema
>;

export const GeneralProductFamilyAttributeType = {
  [GeneralProductFamilyField.title]: AttributeType.ShortText,
  [GeneralProductFamilyField.description]: AttributeType.RichText,
  [GeneralProductFamilyField.vendor]: AttributeType.ShortText,
} satisfies Record<GeneralProductFamilyField, AttributeType>;

export const GeneralProductFamilyAttributeSchema = {
  [GeneralProductFamilyField.title]:
    AttributeValueSchema[
      GeneralProductFamilyAttributeType[GeneralProductFamilyField.title]
    ].nullable(),
  [GeneralProductFamilyField.description]:
    AttributeValueSchema[
      GeneralProductFamilyAttributeType[GeneralProductFamilyField.description]
    ].nullable(),
  [GeneralProductFamilyField.vendor]:
    AttributeValueSchema[
      GeneralProductFamilyAttributeType[GeneralProductFamilyField.vendor]
    ].nullable(),
};

export function getGeneralProductFamilyAttributeType<
  T extends GeneralProductFamilyField,
>(field: T): (typeof GeneralProductFamilyAttributeType)[T] {
  return GeneralProductFamilyAttributeType[field];
}

export type GeneralProductFamilyAttributeValueType = {
  [key in GeneralProductFamilyField]: (typeof GeneralProductFamilyAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getGeneralProductFamilyAttributeValue<
  T extends GeneralProductFamilyField,
>(field: T, value: unknown): GeneralProductFamilyAttributeValueType[T] | null {
  return GeneralProductFamilyAttributeSchema[field].parse(
    value,
  ) as GeneralProductFamilyAttributeValueType[T];
}

export function getProductFamilyAttributeValue<
  T extends GeneralProductFamilyField,
>({
  listing,
  field,
}: {
  listing: ProductFamilyDetails;
  field: T;
}): AttributeValueType[AttributeType] | null {
  let fieldValue;
  switch (field) {
    case GeneralProductFamilyField.title:
      fieldValue = listing.title;
      break;
    case GeneralProductFamilyField.description:
      fieldValue = listing.description;
      break;
    case GeneralProductFamilyField.vendor:
      fieldValue = listing.vendor;
      break;
    default:
      assertNever(field);
  }

  return GeneralProductFamilyAttributeSchema[field].parse(fieldValue);
}
